// Commission ledger, claims and invoices.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Textarea,
  toast,
} from '@tatra/ui';
import { useState } from 'react';
import { FormError, Loading, Stat } from '../components.js';
import {
  api,
  ApiError,
  errorMessage,
  get,
  openInvoice,
  post,
  type Claim,
  type Entry,
  type Ledger,
} from '../lib/api.js';
import { CLAIM_STATUS, dateOnly, ENTRY_STATUS, rupees } from '../lib/format.js';
import { useSession } from '../lib/session.js';

export function CommissionsPage() {
  const { isOwner, approved } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [asking, setAsking] = useState(false);
  const q = useQuery({ queryKey: ['commissions'], queryFn: () => get<Ledger>('/commissions') });
  const claim = useMutation({
    mutationFn: () => post<Claim>('/claims', { note: note.trim() || null }),
    onSuccess: (c) => {
      toast.success(`Claim ${c.number} raised`);
      void qc.invalidateQueries({ queryKey: ['commissions'] });
      void qc.invalidateQueries({ queryKey: ['claims'] });
      void navigate({ to: '/claims/$id', params: { id: c.id } });
    },
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState onRetry={() => void q.refetch()} />;
  const { balance, entries } = q.data;
  return (
    <>
      <PageHeader
        title="Commissions"
        description={
          isOwner
            ? 'What your firm has earned, claimed and been paid.'
            : 'The commission on your own customers.'
        }
        actions={
          isOwner && (
            <Link to="/claims">
              <Button variant="secondary">All claims</Button>
            </Link>
          )
        }
      />
      {balance && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Ready to claim" value={rupees(balance.unclaimed)} />
            <Stat label="Waiting for approval" value={rupees(balance.claimed)} />
            <Stat label="Approved, to be paid" value={rupees(balance.approved)} />
            <Stat label="Paid to you" value={rupees(balance.paid)} />
          </div>
          {approved && balance.unclaimed > 0 && (
            <Card className="mb-6 space-y-3 p-4">
              {asking ? (
                <>
                  <Field label="Note for our finance team (optional)">
                    {(p) => (
                      <Textarea
                        {...p}
                        rows={2}
                        maxLength={1000}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    )}
                  </Field>
                  <FormError message={claim.isError ? errorMessage(claim.error) : null} />
                  <div className="flex flex-wrap gap-2">
                    <Button loading={claim.isPending} onClick={() => claim.mutate()}>
                      Claim {rupees(balance.unclaimed)}
                    </Button>
                    <Button variant="ghost" onClick={() => setAsking(false)}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-text">You have {rupees(balance.unclaimed)} ready to claim.</p>
                  <Button onClick={() => setAsking(true)}>Raise a claim</Button>
                </div>
              )}
            </Card>
          )}
        </>
      )}
      <h2 className="mb-2 text-sm font-semibold text-text">Commission by booking</h2>
      {entries.length === 0 ? (
        <EmptyState
          title="No commission yet"
          description="You earn commission when a customer you registered books a home."
        />
      ) : (
        <ul className="space-y-2">
          {entries.map((e) => (
            <EntryRow key={e.id} e={e} />
          ))}
        </ul>
      )}
    </>
  );
}

function EntryRow({ e }: { e: Entry }) {
  const negative = e.amount < 0;
  return (
    <li>
      <Card className="flex flex-wrap items-center justify-between gap-2 p-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-text">{e.customer}</p>
          <p className="text-sm text-text-muted">
            {e.booking.project} · booked {dateOnly(e.booking.bookedOn)}
            {e.booking.cancelled && ' (cancelled)'}
          </p>
          <p className="text-xs text-text-faint">
            {e.kind === 'CLAWBACK'
              ? 'Taken back after a cancellation'
              : e.kind === 'ADJUSTMENT'
                ? 'Slab adjustment'
                : (e.plan ?? 'Commission')}
            {e.note ? ` · ${e.note}` : ''}
          </p>
        </div>
        <div className="text-right">
          <p className={`font-semibold ${negative ? 'text-danger' : 'text-text'}`}>{rupees(e.amount)}</p>
          <Badge tone={e.status === 'REVERSED' ? 'danger' : e.status === 'CLAIMED' ? 'info' : 'success'}>
            {ENTRY_STATUS[e.status] ?? e.status}
          </Badge>
        </div>
      </Card>
    </li>
  );
}

export function ClaimsPage() {
  const q = useQuery({
    queryKey: ['claims'],
    queryFn: () => get<{ items: Claim[]; total: number }>('/claims?limit=100'),
  });
  return (
    <>
      <PageHeader
        title="Claims"
        description="Each claim is approved by our finance team, then you send an invoice and we pay."
      />
      {q.isLoading ? (
        <Loading />
      ) : q.isError || !q.data ? (
        <ErrorState onRetry={() => void q.refetch()} />
      ) : q.data.items.length === 0 ? (
        <EmptyState
          title="No claims yet"
          description="Raise a claim from the Commissions page when you have commission ready."
          action={
            <Link to="/commissions">
              <Button>Go to commissions</Button>
            </Link>
          }
        />
      ) : (
        <ul className="space-y-2">
          {q.data.items.map((c) => (
            <li key={c.id}>
              <Link
                to="/claims/$id"
                params={{ id: c.id }}
                className="block rounded-xl border border-border bg-surface p-3 hover:bg-surface-hover"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-text">{c.number}</span>
                  <Badge tone={CLAIM_STATUS[c.status]?.tone}>{CLAIM_STATUS[c.status]?.label}</Badge>
                </div>
                <p className="mt-1 text-sm text-text-muted">
                  {rupees(c.amount)} · {c.entries} booking{c.entries === 1 ? '' : 's'} · raised{' '}
                  {dateOnly(c.createdAt)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function ClaimDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery({
    queryKey: ['claim', id],
    queryFn: () => get<Claim & { lines: Entry[] }>(`/claims/${id}`),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data)
    return (
      <ErrorState
        title={
          q.error instanceof ApiError && q.error.status === 404 ? 'We could not find that claim' : undefined
        }
        onRetry={() => void q.refetch()}
      />
    );
  const c = q.data;
  return (
    <>
      <p className="mb-2 text-sm">
        <Link to="/claims" className="text-primary underline">
          All claims
        </Link>
      </p>
      <PageHeader
        title={c.number}
        description={`${rupees(c.amount)} · raised ${dateOnly(c.createdAt)}`}
        actions={<Badge tone={CLAIM_STATUS[c.status]?.tone}>{CLAIM_STATUS[c.status]?.label}</Badge>}
      />
      <div className="space-y-4">
        {c.decisionNote && (
          <p className="rounded-md bg-surface-hover p-3 text-sm text-text">
            <span className="font-medium">Note from our team: </span>
            {c.decisionNote}
          </p>
        )}
        {c.status === 'APPROVED' && <InvoiceForm claim={c} />}
        {(c.status === 'INVOICED' || c.status === 'PAID') && (
          <Card className="space-y-2 p-4 text-sm">
            <h2 className="font-medium text-text">Invoice</h2>
            <p className="text-text-muted">
              {c.invoiceNumber} dated {dateOnly(c.invoiceDate)} for {rupees(c.invoiceAmount)}
            </p>
            {c.hasInvoice && (
              <Button
                variant="secondary"
                onClick={() => void openInvoice(c.id).catch((e) => toast.error(errorMessage(e)))}
              >
                View the invoice
              </Button>
            )}
            {c.status === 'PAID' && (
              <p className="text-text">
                Paid on {dateOnly(c.paidOn)}: {rupees(c.netPaid)} after tax deducted at source of{' '}
                {rupees(c.tdsAmount)}. Reference {c.paymentReference}.
              </p>
            )}
          </Card>
        )}
        <section aria-labelledby="lines-h">
          <h2 id="lines-h" className="mb-2 text-sm font-semibold text-text">
            What this claim covers
          </h2>
          <ul className="space-y-2">
            {c.lines.map((e) => (
              <EntryRow key={e.id} e={e} />
            ))}
          </ul>
        </section>
      </div>
    </>
  );
}

function InvoiceForm({ claim }: { claim: Claim }) {
  const qc = useQueryClient();
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(String(claim.amount / 100));
  const [file, setFile] = useState<File | null>(null);
  const send = useMutation({
    mutationFn: () => {
      const body = new FormData();
      body.append('invoiceNumber', number);
      body.append('invoiceDate', date);
      body.append('invoiceAmount', String(Math.round(Number(amount) * 100)));
      if (file) body.append('file', file);
      return api('POST', `/claims/${claim.id}/invoice`, body);
    },
    onSuccess: () => {
      toast.success('Invoice sent');
      void qc.invalidateQueries({ queryKey: ['claim', claim.id] });
      void qc.invalidateQueries({ queryKey: ['claims'] });
    },
  });
  return (
    <Card className="space-y-4 p-4">
      <div>
        <h2 className="font-medium text-text">Send your invoice</h2>
        <p className="text-sm text-text-muted">
          Your claim is approved. Upload your tax invoice for at least {rupees(claim.amount)} (GST included)
          and we will arrange payment.
        </p>
      </div>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          send.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Invoice number" required>
            {(p) => (
              <Input
                {...p}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                maxLength={60}
                required
              />
            )}
          </Field>
          <Field label="Invoice date" required>
            {(p) => (
              <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            )}
          </Field>
          <Field label="Invoice total (₹)" required>
            {(p) => (
              <Input
                {...p}
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            )}
          </Field>
        </div>
        <Field label="Invoice file (PDF or image, up to 10 MB)" required>
          {(p) => (
            <Input
              {...p}
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              required
            />
          )}
        </Field>
        <FormError message={send.isError ? errorMessage(send.error) : null} />
        <Button type="submit" loading={send.isPending}>
          Send invoice
        </Button>
      </form>
    </Card>
  );
}
